// ─── Granular Permission System ──────────────────────────────────────
// Each team member gets:
//   1. Section-level access (which pages they can visit)
//   2. Per-section granular config (what data they see, view vs edit, etc.)
// ─────────────────────────────────────────────────────────────────────

// ---------- Section definitions ----------

export type SectionPermission = {
  id: string
  label: string
  description: string
  defaultForManager: boolean
  defaultForSupport: boolean
  /** Granular controls available for this section */
  granularOptions?: GranularOptionDef[]
}

export type GranularOptionDef =
  | { type: 'accessLevel' }
  | { type: 'financialAccess' }
  | { type: 'regionFilter' }
  | { type: 'dateFilter' }
  | { type: 'showFilter' }
  | { type: 'actionPermissions' }
  | { type: 'teamActionPermissions' }
  | { type: 'tasksViewScope' }
  | { type: 'tasksActionPermissions' }

export const AVAILABLE_SECTIONS: SectionPermission[] = [
  {
    id: 'overview',
    label: 'Overview',
    description: 'View dashboard and key metrics',
    defaultForManager: true,
    defaultForSupport: true,
    granularOptions: [{ type: 'accessLevel' }, { type: 'financialAccess' }],
  },
  {
    id: 'schedule',
    label: 'Schedule',
    description: 'View and manage calendar events',
    defaultForManager: true,
    defaultForSupport: true,
    granularOptions: [{ type: 'accessLevel' }, { type: 'dateFilter' }],
  },
  {
    id: 'shows',
    label: 'Shows',
    description: 'Manage live performances and tours',
    defaultForManager: true,
    defaultForSupport: false,
    granularOptions: [
      { type: 'accessLevel' },
      { type: 'actionPermissions' },
      { type: 'financialAccess' },
      { type: 'regionFilter' },
      { type: 'dateFilter' },
      { type: 'showFilter' },
    ],
  },
  {
    id: 'music',
    label: 'Music',
    description: 'Manage releases and music catalog',
    defaultForManager: true,
    defaultForSupport: false,
    granularOptions: [{ type: 'accessLevel' }, { type: 'financialAccess' }],
  },
  {
    id: 'finance',
    label: 'Finance',
    description: 'View financial data and reports',
    defaultForManager: false, // Off by default; explicit opt-in for financial access
    defaultForSupport: false,
    granularOptions: [{ type: 'accessLevel' }],
  },
  {
    id: 'deals',
    label: 'Deals',
    description: 'Brand partnerships, sponsorships, and deal pipeline',
    defaultForManager: false,
    defaultForSupport: false,
    granularOptions: [{ type: 'accessLevel' }],
  },
  {
    id: 'tasks',
    label: 'Tasks',
    description: 'Create and manage tasks',
    defaultForManager: true,
    defaultForSupport: true,
    granularOptions: [{ type: 'accessLevel' }, { type: 'tasksViewScope' }, { type: 'tasksActionPermissions' }],
  },
  {
    id: 'links',
    label: 'Links',
    description: 'Shareable links to collect data or distribute releases',
    defaultForManager: true,
    defaultForSupport: false,
    granularOptions: [{ type: 'accessLevel' }],
  },
  {
    id: 'chat',
    label: 'Chat',
    description: 'Team communication',
    defaultForManager: true,
    defaultForSupport: true,
  },
  {
    id: 'knowledge',
    label: 'Guides',
    description: 'Knowledge base and guides',
    defaultForManager: true,
    defaultForSupport: false,
  },
  {
    id: 'activity',
    label: 'Activity',
    description: 'View activity feed, sync status, and approval history',
    defaultForManager: true,
    defaultForSupport: false,
  },
  {
    id: 'team',
    label: 'Team',
    description: 'View team members (invite/remove requires artist role)',
    defaultForManager: true,
    defaultForSupport: false,
    granularOptions: [
      { type: 'accessLevel' },
      { type: 'teamActionPermissions' },
    ],
  },
]

// ---------- Granular permission config per section ----------

export type AccessLevel = 'view' | 'edit'

export type RegionFilter = {
  enabled: boolean
  cities: string[]
  states: string[]
  countries: string[]
}

export type DateFilter = {
  enabled: boolean
  from?: string // ISO date string
  to?: string   // ISO date string
}

export type ShowFilter = {
  enabled: boolean
  mode: 'allowlist' | 'blocklist'
  showIds: string[]   // Booking _id values
}

/** Shows-specific granular config */
export type ShowsGranularConfig = {
  accessLevel: AccessLevel
  financialAccess: boolean     // Can see guarantee, revenue, payout info
  ticketAccess: boolean        // Can see AND update ticket counts / sold %
  regionFilter: RegionFilter   // Restrict to specific regions
  dateFilter: DateFilter       // Restrict to a date range
  showFilter: ShowFilter       // Per-show allowlist / blocklist
  canAddShows?: boolean        // Can create new shows (manual, CSV, Bandsintown)
  canEditShowInfo?: boolean    // Can edit existing show details and delete shows
}

/** Music-specific granular config */
export type MusicGranularConfig = {
  accessLevel: AccessLevel
  financialAccess: boolean     // Can see royalties, stream revenue
}

/** Schedule-specific granular config */
export type ScheduleGranularConfig = {
  accessLevel: AccessLevel
  dateFilter: DateFilter
}

/** Overview-specific granular config */
export type OverviewGranularConfig = {
  accessLevel: AccessLevel
  financialAccess: boolean
}

/** Finance-specific granular config */
export type FinanceGranularConfig = {
  accessLevel: AccessLevel
}

/** Deals / brand pipeline granular config */
export type DealsGranularConfig = {
  accessLevel: AccessLevel
}

/** Tasks-specific granular config */
export type TasksGranularConfig = {
  accessLevel: AccessLevel
  /** When 'mine', team member only sees tasks assigned to them. When 'all', they see all team tasks. */
  viewScope?: 'all' | 'mine'
  /** Can assign tasks to other team members (see assignee dropdown). If false, no assign UI / only self. */
  canAssignTasks?: boolean
  /** Can create new tasks at all. If false, hide New Task and creation flows. */
  canCreateTasks?: boolean
  /** When 'self_only', can only create tasks assigned to themselves (or unassigned). When 'anyone', can assign to any team member. */
  createTaskScope?: 'anyone' | 'self_only'
}

/** Knowledge-specific granular config */
export type KnowledgeGranularConfig = {
  accessLevel: AccessLevel
}

/** Team-specific granular config */
export type TeamGranularConfig = {
  accessLevel: AccessLevel
  canInviteMembers: boolean
  canRemoveMembers: boolean
  canEditPermissions: boolean
  canApproveEdits?: boolean
  canApproveAccessRequests?: boolean
  canApproveWorkflowRequests?: boolean
}

/** The full granular config map (keyed by section ID) */
export type SectionGranularConfig = {
  overview?: OverviewGranularConfig
  shows?: ShowsGranularConfig
  music?: MusicGranularConfig
  schedule?: ScheduleGranularConfig
  finance?: FinanceGranularConfig
  deals?: DealsGranularConfig
  tasks?: TasksGranularConfig
  knowledge?: KnowledgeGranularConfig
  team?: TeamGranularConfig
}

// ---------- Full team member permissions ----------

export type TeamMemberPermissions = {
  /** Array of section IDs the member can access */
  sections: string[]
  /** Per-section granular config (optional — missing = full access for that section) */
  sectionConfig?: SectionGranularConfig
}

// ---------- Defaults ----------

export type TeamMemberRole = 'manager' | 'support' | 'promoter'

export function getDefaultPermissions(role: TeamMemberRole): string[] {
  if (role === 'promoter') {
    return ['shows', 'team', 'activity']
  }
  return AVAILABLE_SECTIONS.filter(
    (section) => role === 'manager' ? section.defaultForManager : section.defaultForSupport
  ).map((section) => section.id)
}

export function getDefaultSectionConfig(role: TeamMemberRole): SectionGranularConfig {
  if (role === 'promoter') {
    return {
      shows: {
        accessLevel: 'edit',
        financialAccess: false, // Off by default; explicit opt-in on Invite Team Member
        ticketAccess: true,
        regionFilter: { enabled: false, cities: [], states: [], countries: [] },
        dateFilter: { enabled: false },
        showFilter: { enabled: true, mode: 'allowlist', showIds: [] },
        canAddShows: true,
        canEditShowInfo: false,
      },
      team: {
        accessLevel: 'view',
        canInviteMembers: false,
        canRemoveMembers: false,
        canEditPermissions: false,
        canApproveEdits: false,
        canApproveAccessRequests: false,
        canApproveWorkflowRequests: false,
      },
    }
  }
  if (role === 'manager') {
    return {
      overview: { accessLevel: 'edit', financialAccess: false },
      shows: {
        accessLevel: 'edit',
        financialAccess: false,
        ticketAccess: true,
        regionFilter: { enabled: false, cities: [], states: [], countries: [] },
        dateFilter: { enabled: false },
        showFilter: { enabled: false, mode: 'allowlist', showIds: [] },
        canAddShows: true,
        canEditShowInfo: true,
      },
      music: { accessLevel: 'edit', financialAccess: false },
      schedule: { accessLevel: 'edit', dateFilter: { enabled: false } },
      finance: { accessLevel: 'edit' },
      deals: { accessLevel: 'edit' },
      tasks: { accessLevel: 'edit', viewScope: 'all', canAssignTasks: true, canCreateTasks: true, createTaskScope: 'anyone' },
      knowledge: { accessLevel: 'edit' },
      team: {
        accessLevel: 'edit',
        canInviteMembers: true,
        canRemoveMembers: true,
        canEditPermissions: true,
        canApproveEdits: true,
        canApproveAccessRequests: true,
        canApproveWorkflowRequests: true,
      },
    }
  }
  // Support — view-only, no financial data
  return {
    overview: { accessLevel: 'view', financialAccess: false },
    shows: {
      accessLevel: 'view',
      financialAccess: false,
      ticketAccess: true,
      regionFilter: { enabled: false, cities: [], states: [], countries: [] },
      dateFilter: { enabled: false },
        showFilter: { enabled: false, mode: 'allowlist', showIds: [] },
        canAddShows: false,
        canEditShowInfo: false,
      },
    music: { accessLevel: 'view', financialAccess: false },
    schedule: { accessLevel: 'view', dateFilter: { enabled: false } },
    finance: { accessLevel: 'view' },
    deals: { accessLevel: 'view' },
    tasks: { accessLevel: 'view', viewScope: 'mine', canAssignTasks: false, canCreateTasks: true, createTaskScope: 'self_only' },
    knowledge: { accessLevel: 'view' },
    team: {
      accessLevel: 'view',
      canInviteMembers: false,
      canRemoveMembers: false,
      canEditPermissions: false,
      canApproveEdits: false,
      canApproveAccessRequests: false,
      canApproveWorkflowRequests: false,
    },
  }
}

/** Build full default TeamMemberPermissions for a role */
export function getDefaultTeamMemberPermissions(role: TeamMemberRole): TeamMemberPermissions {
  return {
    sections: getDefaultPermissions(role),
    sectionConfig: getDefaultSectionConfig(role),
  }
}
